import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Save, Trash2, KeyRound, Smartphone, ShieldCheck, Users, Copy, Check, Loader2, Plus, RefreshCw,
  Boxes, Link2, Sparkles, Info, Power, Activity, Lock,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import {
  productsService, Product, ProductPayload, ProductApiKey, IssuedKey, OtpTemplateShape, ProductOtpTemplate,
} from '@/services/products.service';
import { whatsappAccountService, WhatsappAccount } from '@/services/whatsapp-account.service';
import { templatesService, Template } from '@/services/templates.service';
import { productCredentialsService, Credential } from '@/services/product-credentials.service';
import { FEATURES, SHAPES, Spark, initials, relative } from './productShared';

const emptyForm = (inUse?: WhatsappAccount): ProductPayload => ({
  name: '', key: '', description: '', iconUrl: '', websiteUrl: '', playStoreUrl: '', appStoreUrl: '',
  whatsappAccountUid: inUse?.accountUid ?? '', otpTemplateName: '', otpTemplateLanguage: 'en_US',
  otpTemplateShape: 'code_plus_url_button', otpTemplates: [], features: ['otp'], isActive: true,
  credentialUids: [], aiEnabled: true,
});

const fromProduct = (p: Product): ProductPayload => ({
  name: p.name, key: p.key, description: p.description ?? '', iconUrl: p.iconUrl ?? '', websiteUrl: p.websiteUrl ?? '',
  playStoreUrl: p.playStoreUrl ?? '', appStoreUrl: p.appStoreUrl ?? '',
  whatsappAccountUid: p.whatsappAccountUid, otpTemplateName: p.otpTemplateName,
  otpTemplateLanguage: p.otpTemplateLanguage, otpTemplateShape: p.otpTemplateShape,
  otpTemplates: p.otpTemplates ?? [], features: p.features, isActive: p.isActive,
  credentialUids: p.credentialUids ?? [], aiEnabled: p.aiEnabled ?? true,
  ...(p.aiDelivery ? { aiDelivery: p.aiDelivery } : {}),
});

export default function ProductDetailPage() {
  const { productUid } = useParams<{ productUid: string }>();
  const isNew = !productUid || productUid === 'new';
  const nav = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductPayload | null>(null);
  const [accounts, setAccounts] = useState<WhatsappAccount[]>([]);
  const [vault, setVault] = useState<Credential[]>([]);
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      whatsappAccountService.list().catch(() => [] as WhatsappAccount[]),
      productCredentialsService.list().catch(() => [] as Credential[]),
      isNew ? Promise.resolve(null) : productsService.list().then((l) => l.find((x) => x.productUid === productUid) ?? null),
    ]).then(([accs, creds, p]) => {
      setAccounts(accs); setVault(creds);
      if (!isNew && !p) { toast.error('Product not found'); nav('/products'); return; }
      setProduct(p);
      const inUse = accs.find((a) => a.isDefault && a.isActive) ?? accs.find((a) => a.isActive) ?? accs[0];
      setForm(p ? fromProduct(p) : emptyForm(inUse));
    });
  }, [productUid, isNew, nav]);

  // Approved AUTHENTICATION templates on the selected number — the only kind Meta allows for OTP.
  useEffect(() => {
    if (!form?.whatsappAccountUid) return;
    setTemplates(null);
    templatesService.list({ accountUid: form.whatsappAccountUid, category: 'authentication', status: 'APPROVED', limit: 100 })
      .then((r) => setTemplates(r.items)).catch(() => setTemplates([]));
  }, [form?.whatsappAccountUid]);

  const set = <K extends keyof ProductPayload>(k: K, v: ProductPayload[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const account = useMemo(() => accounts.find((a) => a.accountUid === form?.whatsappAccountUid), [accounts, form?.whatsappAccountUid]);

  const save = async () => {
    if (!form) return;
    if (!form.name.trim() || !form.whatsappAccountUid || !form.otpTemplateName.trim()) { toast.error('Name, WhatsApp number and OTP template are required'); return; }
    if (isNew && !/^[a-z0-9][a-z0-9-]{1,30}$/.test(form.key ?? '')) { toast.error('Key must be lowercase letters, digits or dashes'); return; }
    const badCtx = (form.otpTemplates ?? []).find((t) => !/^[a-z0-9_]{2,30}$/.test(t.key) || !t.templateName.trim());
    if (badCtx) { toast.error('Each login context needs a key (e.g. admin_login) and a template'); return; }
    setSaving(true);
    try {
      const clean = (v?: string) => (v && v.trim() ? v.trim() : undefined);
      const payload: ProductPayload = { ...form, description: clean(form.description), iconUrl: clean(form.iconUrl), websiteUrl: clean(form.websiteUrl), playStoreUrl: clean(form.playStoreUrl), appStoreUrl: clean(form.appStoreUrl) };
      if (isNew) {
        const created = await productsService.create(payload);
        toast.success('Product added — now issue it an API key');
        nav(`/products/${created.productUid}`, { replace: true });
      } else {
        const { key: _k, ...rest } = payload;
        const updated = await productsService.update(productUid!, rest);
        setProduct((p) => (p ? { ...p, ...updated } : updated));
        toast.success('Saved');
      }
    } catch { /* interceptor toasts */ } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!product) return;
    if (!window.confirm(`Delete ${product.name}? Every API key issued for it is revoked immediately and the app stops being able to send.`)) return;
    try { await productsService.remove(product.productUid); toast.success('Product removed'); nav('/products'); } catch { /* interceptor */ }
  };

  if (!form) {
    return <><Topbar title="Product" breadcrumb="Products / …" /><div className="flex items-center gap-2 p-7 text-sm text-text-muted"><Loader2 size={15} className="animate-spin" /> Loading…</div></>;
  }

  const ctx: ProductOtpTemplate[] = form.otpTemplates ?? [];
  const setCtx = (i: number, patch: Partial<ProductOtpTemplate>) => set('otpTemplates', ctx.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  return (
    <>
      <Topbar
        title={isNew ? 'New product' : form.name || product?.name || 'Product'}
        breadcrumb={`Products / ${isNew ? 'New' : product?.name ?? ''}`}
        description={isNew ? 'Link one of your own apps to this company\'s WhatsApp number, templates and keys.' : `Key ${product?.key} · created ${product ? new Date(product.createdAt).toLocaleDateString() : ''}`}
        right={
          <div className="flex items-center gap-2">
            {!isNew && <Button variant="ghost" onClick={remove} iconLeft={<Trash2 size={14} />}>Delete</Button>}
            <Button onClick={save} disabled={saving} iconLeft={<Save size={14} />}>{saving ? 'Saving…' : isNew ? 'Create product' : 'Save changes'}</Button>
          </div>
        }
      />

      <div className="px-7 pb-24 pt-6">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          {/* ── Left: the form, one card per concern ─────────────────────── */}
          <div className="space-y-5">
            <Card>
              <SectionHeader title="Identity" subtitle="How the product appears here and in logs." icon={<Boxes size={16} />} />
              <div className="mb-4 flex items-start gap-4">
                {form.iconUrl
                  ? <img src={form.iconUrl} alt="" className="h-20 w-20 shrink-0 rounded-2xl border border-border object-cover" />
                  : <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-xl font-bold text-primary">{initials(form.name || 'P')}</span>}
                <div className="min-w-0 flex-1">
                  <div className="mb-1 text-sm font-medium text-text-primary">App icon</div>
                  <p className="mb-2 text-2xs text-text-muted">Square PNG or JPG, at least 256×256. Uploaded to ImageKit and shown on the product card.</p>
                  <MediaUpload mode="image" value={form.iconUrl ?? ''} onChange={(url) => set('iconUrl', url)} folder="products/icons" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Name" required><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="PrepFCPS" autoFocus={isNew} /></FormField>
                <FormField label="Key" required hintBelow hint={isNew ? 'Lowercase, e.g. prepfcps. Becomes the contact tag product:<key>.' : 'Fixed after creation — it is baked into the contact tag.'}>
                  <Input value={form.key} disabled={!isNew} onChange={(e) => set('key', e.target.value.toLowerCase())} placeholder="prepfcps" className="font-mono" />
                </FormField>
                <div className="sm:col-span-2">
                  <FormField label="Description"><Textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What this app is, for your own reference." /></FormField>
                </div>
                <FormField label="Website"><Input value={form.websiteUrl} onChange={(e) => set('websiteUrl', e.target.value)} placeholder="https://" /></FormField>
                <FormField label="Play Store URL"><Input value={form.playStoreUrl} onChange={(e) => set('playStoreUrl', e.target.value)} placeholder="https://play.google.com/store/apps/details?id=…" /></FormField>
                <FormField label="App Store URL"><Input value={form.appStoreUrl} onChange={(e) => set('appStoreUrl', e.target.value)} placeholder="https://apps.apple.com/…" /></FormField>
              </div>
            </Card>

            <Card>
              <SectionHeader title="What this app does through the CRM" subtitle="Decides which API scopes its keys get." icon={<Activity size={16} />} />
              <div className="grid gap-2 sm:grid-cols-2">
                {FEATURES.map((f) => {
                  const on = form.features!.includes(f.id);
                  return (
                    <button key={f.id} type="button" onClick={() => set('features', on ? form.features!.filter((x) => x !== f.id) : [...form.features!, f.id])}
                      className={clsx('flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors', on ? 'border-primary bg-primary-soft/40' : 'border-border hover:border-border-strong hover:bg-surface-alt/50')}>
                      <span className={clsx('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', on ? 'bg-primary text-white' : 'bg-surface-alt text-text-muted')}>{on ? <Check size={14} /> : f.icon}</span>
                      <span><span className="block text-sm font-medium text-text-primary">{f.label}</span><span className="block text-2xs leading-relaxed text-text-muted">{f.hint}</span></span>
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card>
              <SectionHeader title="WhatsApp sending" subtitle="Which of this company's numbers the app sends from, and the approved OTP template." icon={<Smartphone size={16} />} />
              <div className="space-y-5">
                <div>
                  <FormField label="Sends from" required>
                    <Select value={form.whatsappAccountUid} onChange={(e) => set('whatsappAccountUid', e.target.value)}>
                      <option value="">— choose a number —</option>
                      {accounts.map((a) => (
                        <option key={a.accountUid} value={a.accountUid}>{a.verifiedName || a.businessName} · {a.displayPhoneNumber}{a.isDefault ? ' · in use' : ''}{!a.isActive ? ' · inactive' : ''}</option>
                      ))}
                    </Select>
                  </FormField>
                  {account && (
                    <div className="mt-2 flex items-center gap-3 rounded-xl bg-surface-alt/60 px-3.5 py-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-success-soft text-success"><Smartphone size={15} /></span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-text-primary">{account.verifiedName || account.businessName}</div>
                        <div className="font-mono text-xs text-text-muted">{account.displayPhoneNumber}{account.isDefault ? ' · the CRM\'s in-use number' : ''}</div>
                      </div>
                    </div>
                  )}
                  <p className="mt-1.5 text-2xs text-text-muted">Several products can share one number. The OTP template must be approved on this number's WhatsApp Business Account.</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_140px]">
                  <FormField label="OTP template" required hintBelow hint={templates === null ? 'Loading approved authentication templates…' : templates.length === 0 ? 'No approved authentication template on this number — type its name if it exists on the WABA.' : `${templates.length} approved authentication template${templates.length === 1 ? '' : 's'} on this number.`}>
                    {templates && templates.length > 0 ? (
                      <Select value={form.otpTemplateName} onChange={(e) => { const t = templates.find((x) => x.name === e.target.value); set('otpTemplateName', e.target.value); if (t) set('otpTemplateLanguage', t.language); }}>
                        <option value="">— choose —</option>
                        {templates.map((t) => <option key={t.templateUid} value={t.name}>{t.name} ({t.language})</option>)}
                        {form.otpTemplateName && !templates.some((t) => t.name === form.otpTemplateName) && <option value={form.otpTemplateName}>{form.otpTemplateName} (custom)</option>}
                      </Select>
                    ) : (
                      <Input value={form.otpTemplateName} onChange={(e) => set('otpTemplateName', e.target.value)} placeholder="wp_otp_template" className="font-mono" />
                    )}
                  </FormField>
                  <FormField label="Language"><Input value={form.otpTemplateLanguage} onChange={(e) => set('otpTemplateLanguage', e.target.value)} placeholder="en_US" className="font-mono" /></FormField>
                </div>

                <div>
                  <div className="mb-1.5 text-sm font-medium text-text-primary">Template layout</div>
                  <p className="mb-2 text-2xs text-text-muted">Must match how the approved template was built — Meta rejects a send whose variable count differs.</p>
                  <div className="space-y-1.5">
                    {SHAPES.map((sh) => {
                      const on = form.otpTemplateShape === sh.id;
                      return (
                        <button key={sh.id} type="button" onClick={() => set('otpTemplateShape', sh.id)}
                          className={clsx('flex w-full items-start gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors', on ? 'border-primary bg-primary-soft/40' : 'border-border hover:border-border-strong hover:bg-surface-alt/50')}>
                          <span className={clsx('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', on ? 'border-primary bg-primary' : 'border-border-strong')}>{on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}</span>
                          <span><span className="block text-sm text-text-primary">{sh.label}</span><span className="block font-mono text-2xs text-text-muted">{sh.example}</span></span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <SectionHeader
                title="Other login contexts"
                subtitle="The template above is used when the app asks for app_login. Add a context for any surface that needs different wording — an admin panel, say — and the app sends its key to pick it."
                icon={<ShieldCheck size={16} />}
                action={<Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => set('otpTemplates', [...ctx, { key: '', label: '', templateName: '', language: form.otpTemplateLanguage ?? 'en_US', shape: 'code_plus_url_button' }])}>Add context</Button>}
              />
              {ctx.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-xs text-text-muted">Only app_login for now. That's fine for most apps.</div>
              ) : (
                <div className="space-y-3">
                  {ctx.map((t, i) => (
                    <div key={i} className="rounded-xl border border-border p-3.5">
                      <div className="grid gap-3 sm:grid-cols-[160px_minmax(0,1fr)_auto]">
                        <FormField label="Context key"><Input value={t.key} onChange={(e) => setCtx(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} placeholder="admin_login" className="font-mono" /></FormField>
                        <FormField label="Template">
                          {templates && templates.length > 0 ? (
                            <Select value={t.templateName} onChange={(e) => { const tpl = templates.find((x) => x.name === e.target.value); setCtx(i, { templateName: e.target.value, ...(tpl ? { language: tpl.language } : {}) }); }}>
                              <option value="">— choose —</option>
                              {templates.map((tpl) => <option key={tpl.templateUid} value={tpl.name}>{tpl.name} ({tpl.language})</option>)}
                            </Select>
                          ) : <Input value={t.templateName} onChange={(e) => setCtx(i, { templateName: e.target.value })} className="font-mono" placeholder="admin_otp_template" />}
                        </FormField>
                        <div className="flex items-end"><button type="button" onClick={() => set('otpTemplates', ctx.filter((_, idx) => idx !== i))} className="rounded-lg p-2 text-text-muted hover:bg-danger-soft hover:text-danger" title="Remove"><Trash2 size={14} /></button></div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {SHAPES.map((sh) => (
                          <button key={sh.id} type="button" onClick={() => setCtx(i, { shape: sh.id as OtpTemplateShape })}
                            className={clsx('rounded-full border px-2.5 py-1 text-2xs transition-colors', t.shape === sh.id ? 'border-primary bg-primary-soft/60 text-primary' : 'border-border text-text-muted hover:text-text-primary')}>{sh.label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <SectionHeader
                title="Keys delivered at startup"
                subtitle="Same idea as the WhatsApp number: pick which vault keys this app receives when it boots."
                icon={<KeyRound size={16} />}
                action={
                  <label className="inline-flex items-center gap-2 text-xs text-text-secondary">
                    <input type="checkbox" checked={form.aiEnabled !== false} onChange={(e) => set('aiEnabled', e.target.checked)} className="accent-primary" />
                    <Sparkles size={12} /> AI features enabled
                  </label>
                }
              />
              {vault.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-xs text-text-muted">The vault is empty — add keys on the Products page first.</div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {vault.map((c) => {
                    const on = (form.credentialUids ?? []).includes(c.credentialUid);
                    const extras = Object.keys(c.extras ?? {}).length;
                    return (
                      <button key={c.credentialUid} type="button" disabled={!c.isActive}
                        onClick={() => set('credentialUids', on ? (form.credentialUids ?? []).filter((x) => x !== c.credentialUid) : [...(form.credentialUids ?? []), c.credentialUid])}
                        className={clsx('flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors disabled:opacity-50', on ? 'border-primary bg-primary-soft/40' : 'border-border hover:border-border-strong hover:bg-surface-alt/50')}>
                        <span className={clsx('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', on ? 'bg-primary text-white' : 'bg-surface-alt text-text-muted')}>{on ? <Check size={13} /> : <KeyRound size={13} />}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-text-primary">{c.label}{!c.isActive ? ' · disabled' : ''}</span>
                          <span className="block truncate font-mono text-2xs text-text-muted">{c.envName}{extras ? ` +${extras}` : ''}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="mt-4">
                <div className="mb-1.5 text-xs font-medium text-text-secondary">How the app uses its AI keys</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    { v: 'proxy' as const, l: 'Through the CRM (recommended)', h: 'The app gets only its product key. AI calls go through the CRM, provider keys never leave it, and a key change here applies on the next call.' },
                    { v: 'env' as const, l: 'Hand keys to the app', h: 'The app receives the raw keys at boot and calls providers directly. Key changes need an app restart.' },
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => set('aiDelivery', o.v)}
                      className={clsx('rounded-xl border px-3.5 py-3 text-left transition-colors', (form.aiDelivery ?? 'env') === o.v ? 'border-primary bg-primary-soft/40' : 'border-border hover:border-border-strong')}>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">{o.v === 'proxy' && <Lock size={12} />}{o.l}</span>
                      <span className="block text-2xs text-text-muted">{o.h}</span>
                    </button>
                  ))}
                </div>
              </div>
              <p className="mt-3 flex items-start gap-1.5 text-2xs text-text-muted"><Info size={12} className="mt-0.5 shrink-0" /> {form.aiDelivery === 'proxy'
                ? 'Unticking "AI features enabled" stops every AI call from this app immediately. Non-AI keys (ImageKit, AWS…) are still handed over at boot. The app picks up a mode change on restart.'
                : 'Unticking "AI features enabled" tells the app to stop calling AI on its next config poll, within about five minutes. Key changes need an app restart.'}</p>
            </Card>

            {!isNew && (
              <Card>
                <SectionHeader title="Status" icon={<Power size={16} />} />
                <div className="grid gap-2 sm:grid-cols-2">
                  {[{ v: true, l: 'Active', h: 'API keys work; the app can send and fetch config.' }, { v: false, l: 'Paused', h: 'Every send and config fetch is refused. Keys are kept.' }].map((o) => (
                    <button key={String(o.v)} type="button" onClick={() => set('isActive', o.v)}
                      className={clsx('rounded-xl border px-3.5 py-3 text-left transition-colors', form.isActive === o.v ? 'border-primary bg-primary-soft/40' : 'border-border hover:border-border-strong')}>
                      <span className="block text-sm font-medium text-text-primary">{o.l}</span><span className="block text-2xs text-text-muted">{o.h}</span>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => nav('/products')}>Back to products</Button>
              <Button onClick={save} disabled={saving} iconLeft={<Save size={14} />}>{saving ? 'Saving…' : isNew ? 'Create product' : 'Save changes'}</Button>
            </div>
          </div>

          {/* ── Right: live summary, stats, API keys ─────────────────────── */}
          <div className="space-y-5 xl:sticky xl:top-24 xl:self-start">
            <Card>
              <div className="flex items-center gap-3">
                {form.iconUrl
                  ? <img src={form.iconUrl} alt="" className="h-12 w-12 rounded-xl border border-border object-cover" />
                  : <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-soft text-sm font-bold text-primary">{initials(form.name || 'Product')}</span>}
                <div className="min-w-0">
                  <div className="truncate font-semibold text-text-primary">{form.name || 'Product name'}</div>
                  <div className="font-mono text-2xs text-text-muted">{form.key || 'key'}{product ? ` · product:${product.key}` : ''}</div>
                </div>
              </div>
              <dl className="mt-4 space-y-2.5 rounded-xl border border-primary/20 bg-primary-soft/30 px-3.5 py-3">
                <Row icon={<Smartphone size={12} />} label="Sends from" value={account ? `${account.verifiedName || account.businessName} · ${account.displayPhoneNumber}` : '—'} />
                <Row icon={<ShieldCheck size={12} />} label="OTP template" value={form.otpTemplateName ? `${form.otpTemplateName} (${form.otpTemplateLanguage})` : '—'} mono />
                <Row icon={<KeyRound size={12} />} label="Keys delivered" value={`${form.credentialUids?.length ?? 0}${product ? ` · fetched ${relative(product.lastConfigFetchAt)}` : ''}`} />
                {product?.aiDelivery === 'proxy' && <Row icon={<Lock size={12} />} label="AI via CRM proxy" value={`${(product.aiProxyRequests ?? 0).toLocaleString()} calls · last ${relative(product.lastAiProxyAt)}`} />}
                <Row icon={<Users size={12} />} label="Synced contacts" value={(product?.stats?.contacts ?? 0).toLocaleString()} />
                <Row icon={<Link2 size={12} />} label="Contact group" value={product?.contactGroupUid ? 'Auto-created' : 'Created on save'} />
              </dl>
            </Card>

            {product && (
              <Card>
                <SectionHeader title="Last 30 days" icon={<Activity size={16} />} className="mb-3" />
                <div className="grid grid-cols-3 gap-3 text-center">
                  <Tile label="OTPs" value={product.stats?.otpSent30d ?? 0} />
                  <Tile label="Messages" value={product.stats?.transactionalSent30d ?? 0} />
                  <Tile label="Failed" value={product.stats?.failed30d ?? 0} danger={(product.stats?.failed30d ?? 0) > 0} />
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <Spark data={product.stats?.spark ?? []} width={180} height={36} />
                  <span className="text-2xs text-text-muted">last send {relative(product.stats?.lastSentAt)}</span>
                </div>
              </Card>
            )}

            {product ? <ApiKeysPanel product={product} /> : (
              <Card>
                <SectionHeader title="API keys" icon={<KeyRound size={16} />} className="mb-2" />
                <p className="text-xs text-text-muted">Create the product first. You'll issue its API key here, and paste it into the app's environment.</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="inline-flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-text-muted"><span className="text-primary">{icon}</span>{label}</dt>
      <dd className={clsx('mt-0.5 break-words text-xs leading-snug text-text-primary', mono && 'font-mono')}>{value}</dd>
    </div>
  );
}

function Tile({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className="rounded-xl bg-surface-alt/60 px-2 py-2.5">
      <div className={clsx('text-lg font-semibold tabular-nums leading-none', danger ? 'text-danger' : 'text-text-primary')}>{value.toLocaleString()}</div>
      <div className="mt-1 text-2xs text-text-muted">{label}</div>
    </div>
  );
}

function ApiKeysPanel({ product }: { product: Product }) {
  const [keys, setKeys] = useState<ProductApiKey[] | null>(null);
  const [name, setName] = useState(`${product.name} backend`);
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const load = () => productsService.listKeys(product.productUid).then(setKeys).catch(() => setKeys([]));
  useEffect(() => { load(); }, [product.productUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const issue = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { const k = await productsService.issueKey(product.productUid, { name: name.trim() }); setIssued(k); load(); }
    catch { /* interceptor */ } finally { setBusy(false); }
  };
  const revoke = async (k: ProductApiKey) => {
    if (!window.confirm(`Revoke "${k.name}"? The app using it stops sending immediately.`)) return;
    try { await productsService.revokeKey(product.productUid, k.apiKeyUid); toast.success('Key revoked'); load(); } catch { /* interceptor */ }
  };
  const copy = async (text: string, tag: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(tag); setTimeout(() => setCopied(null), 1500); } catch { toast.error('Copy failed — select and copy manually'); }
  };
  // Scopes are frozen at issue time, so a key issued before config:read existed
  // (or before a feature was switched on) silently can't do the new thing.
  const requiredScopes = [
    'config:read',
    ...(product.features.includes('otp') ? ['otp:send'] : []),
    ...(product.features.some((f) => f === 'transactional' || f === 'daily_mcq') ? ['messages:send'] : []),
  ];
  const apiBase = (process.env.REACT_APP_API_BASE ?? window.location.origin).replace(/\/$/, '');
  const envSnippet = issued ? `SLIGHTOR_API_URL=${apiBase}\nSLIGHTOR_API_KEY=${issued.plaintextKey}` : '';

  return (
    <Card id="keys">
      <SectionHeader title="API keys" subtitle="One per deployment of the app. Scopes come from the features above." icon={<KeyRound size={16} />} className="mb-3" />
      {issued ? (
        <div className="space-y-3">
          <div className="rounded-xl border border-warning/40 bg-warning-soft/50 px-3 py-2.5 text-xs text-text-secondary"><strong className="text-text-primary">Copy this key now.</strong> It is shown once and never stored in plain text.</div>
          <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface-alt/60 p-2.5 font-mono text-2xs">
            <span className="truncate text-text-primary">{issued.plaintextKey}</span>
            <Button size="sm" variant="secondary" onClick={() => copy(issued.plaintextKey, 'key')} iconLeft={copied === 'key' ? <Check size={12} /> : <Copy size={12} />}>{copied === 'key' ? 'Copied' : 'Copy'}</Button>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between text-2xs text-text-muted"><span>.env for the {product.name} backend</span><button onClick={() => copy(envSnippet, 'env')} className="inline-flex items-center gap-1 text-primary hover:underline">{copied === 'env' ? <Check size={11} /> : <Copy size={11} />} copy</button></div>
            <pre className="overflow-x-auto rounded-xl border border-border bg-surface-alt/60 p-2.5 font-mono text-2xs text-text-primary">{envSnippet}</pre>
          </div>
          <div className="text-2xs text-text-muted">Scopes: {issued.scopes.join(', ')}</div>
          <Button className="w-full" onClick={() => setIssued(null)}>Done</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name, e.g. Production backend" />
            <Button className="w-full whitespace-nowrap" onClick={issue} disabled={busy || !name.trim()} iconLeft={<KeyRound size={13} />}>{busy ? 'Issuing…' : 'Issue new key'}</Button>
          </div>
          {keys === null ? (
            <div className="flex items-center gap-2 py-3 text-xs text-text-muted"><Loader2 size={13} className="animate-spin" /> Loading keys…</div>
          ) : keys.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-2xs text-text-muted">No keys yet. Issue one and paste it into the app.</div>
          ) : (
            <ul className="divide-y divide-border rounded-xl border border-border">
              {keys.map((k) => (
                <li key={k.apiKeyUid} className={clsx('flex items-center gap-2.5 px-3 py-2', !k.isActive && 'opacity-60')}>
                  <KeyRound size={13} className={k.isActive ? 'text-primary' : 'text-text-muted'} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs"><span className="truncate font-medium text-text-primary">{k.name}</span><span className="font-mono text-2xs text-text-muted">{k.prefix}…</span>{!k.isActive && <span className="rounded-full bg-surface-alt px-1.5 text-2xs text-text-muted">revoked</span>}</div>
                    <div className="truncate text-2xs text-text-muted">used {relative(k.lastUsedAt)} · {k.totalRequests ?? 0} requests</div>
                    {k.isActive && requiredScopes.some((s) => !k.scopes.includes(s)) && (
                      <div className="mt-0.5 text-2xs text-warning">Missing {requiredScopes.filter((s) => !k.scopes.includes(s)).join(', ')} — issue a new key and swap it in.</div>
                    )}
                  </div>
                  {k.isActive && <button onClick={() => revoke(k)} className="rounded-lg p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger" title="Revoke"><Trash2 size={13} /></button>}
                </li>
              ))}
            </ul>
          )}
          <button onClick={load} className="inline-flex items-center gap-1 text-2xs text-text-muted hover:text-text-primary"><RefreshCw size={11} /> refresh</button>
        </div>
      )}
    </Card>
  );
}
